import React, { useCallback, useEffect, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import EventService from '../../services/EventService';
import FeedbackService from '../../services/FeedbackService';
import SummaryKpiCards from './components/feedback-insights/SummaryKpiCards';
import OverallSatisfactionCard from './components/feedback-insights/OverallSatisfactionCard';
import LikelihoodRecommendCard from './components/feedback-insights/LikelihoodRecommendCard';
import ValuableTopicsCard from './components/feedback-insights/ValuableTopicsCard';
import OptionDistributionCard from './components/feedback-insights/OptionDistributionCard';
import VoicesPanel from './components/feedback-insights/VoicesPanel';
import './FeedbackInsights.css';

const PRESENTER_ORDER = ['Excellent', 'Good', 'Average', 'Needs improvement'];
const LENGTH_ORDER = ['Too short', 'Just right', 'Slightly too long', 'Way too long'];
const BENEFIT_ORDER = [
  'Extremely beneficial',
  'Very beneficial',
  'Somewhat beneficial',
  'Slightly beneficial',
  'Not beneficial',
];

const normalizeSummary = (data = {}) => ({
  eventId: data.eventId || data.EventId || '',
  eventName: data.eventName || data.EventName || '',
  totalResponses: Number(data.totalResponses ?? data.TotalResponses ?? 0),
  summary: {
    averageSatisfaction: Number(
      data.summary?.averageSatisfaction ??
        data.Summary?.AverageSatisfaction ??
        data.summary?.averageRating ??
        data.Summary?.AverageRating ??
        0,
    ),
    npsScore: Number(data.summary?.npsScore ?? data.Summary?.NpsScore ?? 0),
    positiveRate: Number(data.summary?.positiveRate ?? data.Summary?.PositiveRate ?? 0),
    promoters: Number(data.summary?.promoters ?? data.Summary?.Promoters ?? 0),
    passives: Number(data.summary?.passives ?? data.Summary?.Passives ?? 0),
    detractors: Number(data.summary?.detractors ?? data.Summary?.Detractors ?? 0),
  },
  overallSatisfaction: data.overallSatisfaction || data.OverallSatisfaction || [],
  likelihoodToRecommend: data.likelihoodToRecommend || data.LikelihoodToRecommend || {},
  valuableTopics: data.valuableTopics || data.ValuableTopics || [],
  presenterDelivery: data.presenterDelivery || data.PresenterDelivery || [],
  webinarLength: data.webinarLength || data.WebinarLength || [],
  benefitSummary: data.benefitSummary || data.BenefitSummary || [],
  criticalVoices: (data.criticalVoices || data.CriticalVoices || []).map((voice) => ({
    name: voice.name || voice.Name || 'Anonymous',
    rating: Number(voice.rating ?? voice.Rating ?? 0),
    comment: voice.comment || voice.Comment || '',
  })),
  positiveVoices: (data.positiveVoices || data.PositiveVoices || []).map((voice) => ({
    name: voice.name || voice.Name || 'Anonymous',
    rating: Number(voice.rating ?? voice.Rating ?? 0),
    comment: voice.comment || voice.Comment || '',
  })),
});

const FeedbackInsights = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [summary, setSummary] = useState(null);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [summaryError, setSummaryError] = useState('');

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    setEventsError('');
    try {
      const response = await EventService.fetchCompletedEvents(10);
      if (response.data?.success) {
        const list = (response.data.data || [])
          .map((event) => ({
            id: event.id || event.Id,
            name: event.name || event.Name || 'Untitled event',
            endDate: event.endDate || event.EndDate || null,
          }))
          .filter((event) => event.id)
          .sort((a, b) => {
            const aTime = a.endDate ? new Date(a.endDate).getTime() : 0;
            const bTime = b.endDate ? new Date(b.endDate).getTime() : 0;
            return bTime - aTime;
          });

        setEvents(list);
        setSelectedEventId(list[0]?.id || '');
      } else {
        setEvents([]);
        setSelectedEventId('');
        setEventsError(response.data?.message || 'Failed to load completed events.');
      }
    } catch (error) {
      console.error('Failed to load completed events for feedback insights:', error);
      setEvents([]);
      setSelectedEventId('');
      setEventsError(
        error.response?.data?.message || error.message || 'Failed to load completed events.',
      );
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  const loadSummary = useCallback(async (eventId) => {
    if (!eventId) {
      setSummary(null);
      return;
    }

    setLoadingSummary(true);
    setSummaryError('');
    try {
      const response = await FeedbackService.fetchEventFeedbackSummary(eventId);
      if (response.data?.success && response.data.data) {
        setSummary(normalizeSummary(response.data.data));
      } else {
        setSummary(null);
        setSummaryError(response.data?.message || 'Failed to load feedback summary.');
        toast.error(response.data?.message || 'Failed to load feedback summary.');
      }
    } catch (error) {
      console.error('Failed to load feedback summary:', error);
      setSummary(null);
      const message =
        error.response?.data?.message || error.message || 'Failed to load feedback summary.';
      setSummaryError(message);
      toast.error(message);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (selectedEventId) {
      loadSummary(selectedEventId);
    }
  }, [selectedEventId, loadSummary]);

  const totalResponses = summary?.totalResponses ?? 0;
  const hasNoEvents = !loadingEvents && events.length === 0;
  const hasEmptyFeedback = !loadingSummary && summary && totalResponses === 0;

  return (
    <div className="feedback-insights">
      <div className="fi-toolbar">
        <div className="fi-event-picker">
          <label className="fi-event-label" htmlFor="fi-event-select">
            Event:
          </label>
          <Form.Select
            id="fi-event-select"
            className="fi-event-select"
            value={selectedEventId}
            disabled={loadingEvents || events.length === 0}
            onChange={(e) => setSelectedEventId(e.target.value)}
            aria-label="Select completed event"
          >
            {events.length === 0 ? (
              <option value="">No completed events available</option>
            ) : (
              events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))
            )}
          </Form.Select>
        </div>

        <div className="fi-response-count">
          {loadingSummary ? 'Loading…' : `${totalResponses} responses`}
        </div>
      </div>

      {loadingEvents ? (
        <div className="fi-state">
          <Spinner animation="border" role="status" />
          <p>Loading events…</p>
        </div>
      ) : null}

      {eventsError ? (
        <div className="fi-state fi-state-error">
          <p>{eventsError}</p>
          <button type="button" className="fi-retry-btn" onClick={loadEvents}>
            Retry
          </button>
        </div>
      ) : null}

      {hasNoEvents && !eventsError ? (
        <div className="fi-state">
          <p>No completed events found. Feedback insights are available after an event ends.</p>
        </div>
      ) : null}

      {!loadingEvents && !eventsError && selectedEventId ? (
        <>
          {loadingSummary ? (
            <div className="fi-state">
              <Spinner animation="border" role="status" />
              <p>Loading feedback insights…</p>
            </div>
          ) : null}

          {summaryError && !loadingSummary ? (
            <div className="fi-state fi-state-error">
              <p>{summaryError}</p>
              <button
                type="button"
                className="fi-retry-btn"
                onClick={() => loadSummary(selectedEventId)}
              >
                Retry
              </button>
            </div>
          ) : null}

          {!loadingSummary && !summaryError && summary ? (
            <>
              {hasEmptyFeedback ? (
                <div className="fi-state">
                  <p>No feedback available for this event yet.</p>
                </div>
              ) : (
                <>
                  <SummaryKpiCards
                    summary={summary.summary}
                    totalResponses={totalResponses}
                  />

                  <div className="fi-grid-2">
                    <OverallSatisfactionCard
                      items={summary.overallSatisfaction}
                      totalResponses={totalResponses}
                    />
                    <LikelihoodRecommendCard
                      likelihood={summary.likelihoodToRecommend}
                      totalResponses={totalResponses}
                    />
                  </div>

                  <ValuableTopicsCard topics={summary.valuableTopics} />

                  <div className="fi-grid-2">
                    <OptionDistributionCard
                      title="Presenter Delivery"
                      subtitle="(Q4)"
                      icon="★"
                      items={summary.presenterDelivery}
                      preferredOrder={PRESENTER_ORDER}
                    />
                    <OptionDistributionCard
                      title="Webinar Length"
                      subtitle="(Q5)"
                      icon="⏳"
                      items={summary.webinarLength}
                      preferredOrder={LENGTH_ORDER}
                    />
                  </div>

                  <OptionDistributionCard
                    title="How Beneficial Was the Webinar"
                    subtitle="(Q6)"
                    icon="📊"
                    items={summary.benefitSummary}
                    preferredOrder={BENEFIT_ORDER}
                    columns={2}
                  />

                  <VoicesPanel
                    criticalVoices={summary.criticalVoices}
                    positiveVoices={summary.positiveVoices}
                  />
                </>
              )}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default FeedbackInsights;
